import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText, X, Filter, Loader2, Search, Building2, User, Briefcase,
  Paperclip, Ban, Send, CalendarClock, Shield, Eye, ExternalLink,
  CheckCircle2, XCircle, Clock,
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import Modal from '../shared/Modal';
import { notifyToast } from '../../lib/toastStore';
import AdminOfferOnBehalfModal from './AdminOfferOnBehalfModal';
import SearchableSelect from '../shared/SearchableSelect';
import AdminInterviewOnBehalfModal from './AdminInterviewOnBehalfModal';

/**
 * Admin Offers console (Employer Phase 3 §7).
 *
 * One place to manage every offer record in the workspace: the table
 * (offer status and PLACEMENT APPROVAL status are separate columns —
 * they are separate facts), the placement-approval queue on top
 * (approve/reject run through the existing placement endpoints), the
 * offer detail with its full event history, revocation with a
 * mandatory admin-only internal note, and the two on-behalf actions
 * (issue an offer / send an interview invitation for an employer).
 */

interface AdminOffersManagerProps {
  canEdit?: boolean;
  onViewApplicant?: (applicationId: string) => void;
}

const OFFER_STATUSES = ['issued', 'accepted', 'declined', 'expired', 'revoked', 'withdrawn'];
const PLACEMENT_STATES = ['pending', 'approved', 'rejected', 'not_applicable'];

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const statusChip: Record<string, string> = {
  issued: 'bg-blue-50 text-blue-700 border-blue-100',
  accepted: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  declined: 'bg-red-50 text-red-700 border-red-100',
  expired: 'bg-slate-100 text-slate-600 border-slate-200',
  revoked: 'bg-amber-50 text-amber-700 border-amber-100',
  withdrawn: 'bg-amber-50 text-amber-700 border-amber-100',
};
const placementChip: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  rejected: 'bg-red-50 text-red-700 border-red-100',
  not_applicable: 'bg-slate-50 text-slate-400 border-slate-100',
};

export default function AdminOffersManager({ canEdit = true, onViewApplicant }: AdminOffersManagerProps = {}) {
  const [offers, setOffers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState<any[]>([]);
  const [employers, setEmployers] = useState<any[]>([]);

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [placementFilter, setPlacementFilter] = useState('');
  const [employerFilter, setEmployerFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [sort, setSort] = useState('issued_desc');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [declineReason, setDeclineReason] = useState<any | null>(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<any | null>(null);
  const [revokeNote, setRevokeNote] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [queueBusy, setQueueBusy] = useState<string | null>(null);

  const [showOfferOnBehalf, setShowOfferOnBehalf] = useState(false);
  const [showInterviewOnBehalf, setShowInterviewOnBehalf] = useState(false);


  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { setPage(1); }, [qDebounced, statusFilter, placementFilter, employerFilter, programFilter, sort]);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (qDebounced) params.set('q', qDebounced);
      if (statusFilter) params.set('status', statusFilter);
      if (placementFilter) params.set('placement', placementFilter);
      if (employerFilter) params.set('employerId', employerFilter);
      if (programFilter) params.set('program', programFilter);
      if (sort) params.set('sort', sort);
      params.set('page', String(page));
      params.set('pageSize', '25');
      const d: any = await safeFetch(`/api/admin/offers?${params.toString()}`);
      setOffers(d.offers || []);
      setTotal(d.total || 0);
      setTotalPages(d.totalPages || 1);
    } catch { /* toast layer surfaces fetch errors */ } finally {
      setLoading(false);
    }
  }, [qDebounced, statusFilter, placementFilter, employerFilter, programFilter, sort, page]);

  const loadQueue = useCallback(async () => {
    try {
      const d: any = await safeFetch('/api/admin/offers/placement-queue');
      setQueue(d.queue || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadOffers(); }, [loadOffers]);
  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => {
    safeFetch('/api/admin/offers/employers')
      .then((d: any) => setEmployers(d.employers || []))
      .catch(() => {});
  }, []);

  const programOptions = useMemo(() => {
    const set = new Set<string>();
    offers.forEach((o) => { if (o.programType) set.add(o.programType); });
    queue.forEach((o) => { if (o.programType) set.add(o.programType); });
    return Array.from(set).sort();
  }, [offers, queue]);

  const openDetail = async (offerId: string) => {
    setSelectedId(offerId);
    setDetail(null);
    setEvents([]);
    setDeclineReason(null);
    setDetailLoading(true);
    try {
      const d: any = await safeFetch(`/api/admin/offers/${offerId}`);
      setDetail(d.offer || null);
      setEvents(d.events || []);
      if (d.offer?.hasDeclineReason && d.offer?.jobApplicationId) {
        try {
          const rd: any = await safeFetch(`/api/admin/job-applications/${d.offer.jobApplicationId}/offer-decline`);
          setDeclineReason(rd.decline || null);
        } catch { /* reason stays hidden if it cannot be loaded */ }
      }
    } catch { /* handled by toast layer */ } finally {
      setDetailLoading(false);
    }
  };

  const refreshAll = useCallback(() => {
    loadOffers();
    loadQueue();
    if (selectedId) openDetail(selectedId);
  }, [loadOffers, loadQueue, selectedId]);

  const reviewAttachment = async () => {
    if (!detail?.id) return;
    setAttachmentBusy(true);
    try {
      const d: any = await safeFetch(`/api/offers/${detail.id}/attachment-url`, { method: 'POST' });
      if (d?.url) window.open(d.url, '_blank', 'noopener');
    } catch (err: any) {
      notifyToast('error', err?.message || 'The attachment could not be opened.');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const decidePlacement = async (row: any, decision: 'Placed' | 'Cancelled', reason?: string) => {
    const applicationId = row.placementApplicationId;
    const placementId = row.placementId;
    if (!applicationId || !placementId) return;
    setQueueBusy(row.id);
    try {
      const payload: any = { status: decision };
      if (decision === 'Cancelled' && reason?.trim()) payload.reason = reason.trim();
      await safeFetch(`/api/applications/${applicationId}/placements/${placementId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      notifyToast('success', decision === 'Placed' ? 'Placement approved.' : 'Placement rejected.');
      setRejectTarget(null);
      setRejectReason('');
      refreshAll();
    } catch (err: any) {
      notifyToast('error', err?.message || 'The placement decision failed.');
    } finally {
      setQueueBusy(null);
    }
  };

  const submitRevoke = async () => {
    if (!revokeTarget) return;
    if (!revokeNote.trim()) {
      notifyToast('error', 'An internal note is required to withdraw an offer.');
      return;
    }
    setRevokeBusy(true);
    try {
      await safeFetch(`/api/admin/offers/${revokeTarget.id}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: revokeNote.trim() }),
      });
      notifyToast('success', `Offer ${revokeTarget.offerRef || ''} withdrawn.`);
      setRevokeTarget(null);
      setRevokeNote('');
      refreshAll();
    } catch (err: any) {
      notifyToast('error', err?.message || 'The offer could not be withdrawn.');
    } finally {
      setRevokeBusy(false);
    }
  };

  const goToTab = (tab: string) => {
    window.dispatchEvent(new CustomEvent('nav:change', { detail: { tab } }));
  };

  const renderPlacementDecisionButtons = (row: any) => {
    if (!canEdit || row.placementApproval !== 'pending' || !row.placementId) return null;
    const busy = queueBusy === row.id;
    return (
      <span className="inline-flex items-center gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); decidePlacement(row, 'Placed'); }}
          disabled={busy}
          className="px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-lg hover:bg-emerald-100 disabled:opacity-50 cursor-pointer transition"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Approve placement'}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setRejectTarget(row); setRejectReason(''); }}
          disabled={busy}
          className="px-2 py-1 bg-red-50 text-red-700 text-[10px] font-bold rounded-lg hover:bg-red-100 disabled:opacity-50 cursor-pointer transition"
        >
          Reject
        </button>
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5 animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-sm font-black text-slate-900 flex items-center gap-2">
          <FileText className="h-4 w-4 text-brand" />
          Offers
          <span className="text-[11px] font-semibold text-slate-500">{total} offer{total !== 1 ? 's' : ''} in this workspace</span>
        </h1>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowOfferOnBehalf(true)}
              className="px-3 py-2 bg-success text-white text-xs font-bold rounded-xl hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] cursor-pointer transition flex items-center gap-1.5"
            >
              <Send className="h-3.5 w-3.5" /> Issue offer on behalf
            </button>
            <button
              onClick={() => setShowInterviewOnBehalf(true)}
              className="px-3 py-2 bg-white border border-slate-200 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-50 cursor-pointer transition flex items-center gap-1.5"
            >
              <CalendarClock className="h-3.5 w-3.5" /> Interview on behalf
            </button>
          </div>
        )}
      </div>

      {/* Placement approval queue */}
      {queue.length > 0 && (
        <div className="bg-amber-50/60 rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-amber-100 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-600" />
            <h3 className="text-xs font-black text-amber-900">Placement approvals waiting ({queue.length})</h3>
            <span className="text-[11px] text-amber-700">An accepted offer's placement stays Pending Approval — and the workflow gate stays blocked — until it is approved here.</span>
          </div>
          <div className="divide-y divide-amber-100/70">
            {queue.map((row) => (
              <div key={row.id} className="px-5 py-3 flex flex-wrap items-center gap-3 hover:bg-amber-50 transition">
                <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5"><User className="h-3 w-3 text-slate-400" />{row.applicantName || '—'}</span>
                <span className="text-xs text-slate-600 flex items-center gap-1.5"><Building2 className="h-3 w-3 text-slate-400" />{row.employerName || '—'}</span>
                <span className="text-xs text-slate-600 flex items-center gap-1.5"><Briefcase className="h-3 w-3 text-slate-400" />{row.jobTitle || '—'}</span>
                <span className="text-[11px] text-slate-500">Offer {row.offerRef || '—'} · accepted {fmtDate(row.respondedAt)}</span>
                <span className="ml-auto flex items-center gap-2">
                  <button onClick={() => openDetail(row.id)} className="px-2 py-1 bg-white text-slate-600 text-[10px] font-bold rounded-lg border border-amber-200 hover:bg-amber-100 cursor-pointer transition">View offer</button>
                  {renderPlacementDecisionButtons(row)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-wrap gap-2 items-center">
        <Filter className="h-3.5 w-3.5 text-slate-400" />
        <span className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search applicant, employer, job, reference…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="text-xs border border-slate-200 rounded-xl pl-8 pr-2.5 py-1.5 bg-white outline-none w-64"
          />
        </span>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none">
          <option value="">All offer statuses</option>
          {OFFER_STATUSES.map((s) => (<option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>))}
        </select>
        <select value={placementFilter} onChange={(e) => setPlacementFilter(e.target.value)} className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none">
          <option value="">All placement approvals</option>
          {PLACEMENT_STATES.map((s) => (<option key={s} value={s}>{s === 'pending' ? 'Pending Approval' : s === 'approved' ? 'Approved — Placed' : s === 'rejected' ? 'Rejected' : 'Not applicable'}</option>))}
        </select>
        <div className="w-44">
          <SearchableSelect value={employerFilter} onChange={(v) => setEmployerFilter(v)}
            options={employers.map((e) => ({ value: e.id, label: e.companyName }))}
            placeholder="Search employers…"
            clearLabel="All employers"
            emptyLabel="No employers found"
          />
        </div>
        <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)} className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none">
          <option value="">All programs</option>
          {programOptions.map((p) => (<option key={p} value={p}>{p}</option>))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-xs border border-slate-200 rounded-xl px-2.5 py-1.5 bg-white outline-none">
          <option value="issued_desc">Newest issued first</option>
          <option value="deadline_asc">Deadline soonest first</option>
          <option value="updated_desc">Recently updated first</option>
        </select>
        {(q || statusFilter || placementFilter || employerFilter || programFilter) && (
          <button
            onClick={() => { setQ(''); setStatusFilter(''); setPlacementFilter(''); setEmployerFilter(''); setProgramFilter(''); }}
            className="text-[11px] font-bold text-slate-500 hover:text-slate-800 cursor-pointer"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
        <div className="min-w-[1180px]">
          <div className="grid grid-cols-[86px_1.2fr_1.2fr_1.2fr_1fr_0.9fr_0.8fr_0.9fr_1.1fr_1fr_0.9fr_0.8fr_64px] gap-3 px-5 py-3 bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            <span>Reference</span>
            <span>Applicant</span>
            <span>Employer</span>
            <span>Job</span>
            <span>Pay</span>
            <span>Program</span>
            <span>Issued</span>
            <span>Deadline</span>
            <span>Offer status</span>
            <span>Placement approval</span>
            <span>Issued by</span>
            <span>Updated</span>
            <span className="text-right">View</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
              <span className="ml-2 text-xs text-slate-500">Loading offers…</span>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {offers.map((o) => (
                <div
                  key={o.id}
                  onClick={() => openDetail(o.id)}
                  className="grid grid-cols-[86px_1.2fr_1.2fr_1.2fr_1fr_0.9fr_0.8fr_0.9fr_1.1fr_1fr_0.9fr_0.8fr_64px] gap-3 px-5 py-3 items-center hover:bg-slate-50 transition cursor-pointer"
                >
                  <span className="text-xs font-bold text-slate-800">{o.offerRef || '—'}</span>
                  <span className="text-xs font-medium text-slate-800 truncate">{o.applicantName || '—'}</span>
                  <span className="text-xs text-slate-600 truncate">{o.employerName || '—'}</span>
                  <span className="text-xs text-slate-600 truncate">{o.jobTitle || '—'}</span>
                  <span className="text-xs font-semibold text-slate-700 truncate" title={o.compensationSource === 'job' ? 'Rate from the job posting (this offer predates offer pay)' : undefined}>
                    {o.compensation || '—'}{o.compensationSource === 'job' ? <span className="block text-[10px] font-semibold text-slate-400">job posting rate</span> : null}
                  </span>
                  <span className="text-xs text-slate-600 truncate">{o.programType || '—'}</span>
                  <span className="text-[11px] text-slate-500">{fmtDate(o.issuedAt)}</span>
                  <span className="text-[11px] text-slate-500">{fmtDate(o.responseDeadline)}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border w-fit ${statusChip[o.status] || statusChip.issued}`}>{o.statusLabel}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border w-fit ${placementChip[o.placementApproval] || placementChip.not_applicable}`}>{o.placementApprovalLabel}</span>
                  <span className="text-[11px] text-slate-500">{o.actorLabel}{o.actingAdminName ? ` · ${o.actingAdminName}` : ''}</span>
                  <span className="text-[11px] text-slate-500">{fmtDate(o.updatedAt)}</span>
                  <span className="text-right">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-lg"><Eye className="h-3 w-3" /> Details</span>
                  </span>
                </div>
              ))}
              {offers.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-500">No offers match these filters.</div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
          <span className="text-[11px] text-slate-500">{total} offer{total !== 1 ? 's' : ''} · page {page} of {totalPages}</span>
          <span className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2.5 py-1 text-[11px] font-bold rounded-lg border border-slate-200 disabled:opacity-40 cursor-pointer">Previous</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-2.5 py-1 text-[11px] font-bold rounded-lg border border-slate-200 disabled:opacity-40 cursor-pointer">Next</button>
          </span>
        </div>
      </div>

      {/* Detail modal */}
      {selectedId && (
        <Modal onClose={() => setSelectedId(null)} title="Offer details">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-sm font-black text-slate-900">Offer {detail?.offerRef || ''}</h3>
                <p className="text-[11px] text-slate-500">{detail ? `${detail.applicantName || 'Applicant'} · ${detail.jobTitle || 'Job'} · ${detail.employerName || 'Employer'}` : 'Loading…'}</p>
              </div>
              <button aria-label="Close" title="Close" onClick={() => setSelectedId(null)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            {detailLoading || !detail ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 text-slate-400 animate-spin" /><span className="ml-2 text-xs text-slate-500">Loading offer…</span></div>
            ) : (
              <div className="p-5 space-y-5">
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    ['Offer status', detail.statusLabel],
                    ['Placement approval', detail.placementApprovalLabel],
                    ['Pay', detail.compensation ? (detail.compensationSource === 'job' ? `${detail.compensation} (job posting rate)` : detail.compensation) : '—'],
                    ['Issued', fmtDateTime(detail.issuedAt)],
                    ['Response deadline', fmtDateTime(detail.responseDeadline)],
                    ['Responded', fmtDateTime(detail.respondedAt)],
                    ['Issued by', detail.issuedBy === 'admin_on_behalf' ? `Admin on behalf${detail.actingAdminName ? ` — ${detail.actingAdminName}` : ''}` : 'Employer'],
                    ['Program', detail.programType || '—'],
                    ['Last updated', fmtDateTime(detail.updatedAt)],
                  ].map(([label, value]) => (
                    <div key={label as string} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
                      <p className="text-sm font-bold text-slate-800 mt-1 break-words">{value || '—'}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  {detail.applicationId && onViewApplicant && (
                    <button onClick={() => onViewApplicant(detail.placementApplicationId || detail.applicationId)} className="px-3 py-2 bg-blue-50 text-blue-700 text-xs font-bold rounded-xl hover:bg-blue-100 cursor-pointer transition flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5" /> View applicant record <ExternalLink className="h-3 w-3" />
                    </button>
                  )}
                  <button onClick={() => goToTab('employers')} className="px-3 py-2 bg-slate-50 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-100 cursor-pointer transition flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" /> Open Employers
                  </button>
                  <button onClick={() => goToTab('jobs')} className="px-3 py-2 bg-slate-50 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-100 cursor-pointer transition flex items-center gap-1.5">
                    <Briefcase className="h-3.5 w-3.5" /> Open Jobs
                  </button>
                  {detail.attachment && (
                    <button onClick={reviewAttachment} disabled={attachmentBusy} className="px-3 py-2 bg-slate-50 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-100 disabled:opacity-50 cursor-pointer transition flex items-center gap-1.5">
                      {attachmentBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                      Review offer letter ({detail.attachment.name})
                    </button>
                  )}
                </div>

                {detail.details && (
                  <div className="rounded-xl border border-slate-100 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Message sent with the offer</p>
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-line">{detail.details}</p>
                  </div>
                )}

                {detail.hasDeclineReason && (
                  <div className="rounded-xl border border-red-100 bg-red-50/50 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-red-400 flex items-center gap-1"><Shield className="h-3 w-3" /> Applicant's decline reason — admin only, never shared with the employer</p>
                    <p className="text-sm text-slate-800 mt-1 whitespace-pre-line">{declineReason?.reason || (detailLoading ? 'Loading…' : 'Reason recorded — reopen to load.')}</p>
                    {declineReason?.declinedAt && <p className="text-[11px] text-slate-500 mt-1">Declined {fmtDateTime(declineReason.declinedAt)}</p>}
                  </div>
                )}

                {(detail.status === 'revoked' || detail.status === 'withdrawn') && (
                  <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500 flex items-center gap-1"><Shield className="h-3 w-3" /> {detail.revokedByRole === 'employer' ? 'Withdrawn by the employer — reason given (admin only)' : 'Withdrawn by ISATS — internal note (admin only)'}</p>
                    <p className="text-sm text-slate-800 mt-1 whitespace-pre-line">{detail.revocationNote || '—'}</p>
                    <p className="text-[11px] text-slate-500 mt-1">Withdrawn {fmtDateTime(detail.revokedAt)}{detail.revokedByName ? ` by ${detail.revokedByName}` : ''}. {detail.revokedByRole === 'employer' ? 'The applicant was told only that the offer was withdrawn.' : 'The applicant and employer were told only that the offer was withdrawn.'}</p>
                  </div>
                )}

                {detail.placementApproval === 'pending' && canEdit && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 flex flex-wrap items-center gap-3">
                    <p className="text-xs font-bold text-amber-900">This placement is waiting for approval.</p>
                    <span className="ml-auto">{renderPlacementDecisionButtons(detail)}</span>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Event history</h4>
                  {events.length === 0 ? (
                    <p className="text-xs text-slate-500">No events recorded yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {events.map((ev, i) => (
                        <div key={i} className="flex gap-3 items-start rounded-xl border border-slate-100 p-3">
                          <span className="mt-0.5">
                            {ev.type === 'issued' ? <Send className="h-3.5 w-3.5 text-blue-500" />
                              : ev.type === 'accepted' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                              : ev.type === 'declined' ? <XCircle className="h-3.5 w-3.5 text-red-500" />
                              : ev.type === 'revoked' ? <Ban className="h-3.5 w-3.5 text-amber-500" />
                              : <Clock className="h-3.5 w-3.5 text-slate-400" />}
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-800">{ev.title}</p>
                            <p className="text-[11px] text-slate-500">{ev.actor} · {fmtDateTime(ev.at)}</p>
                            {ev.note && <p className="text-[11px] text-slate-600 mt-0.5">{ev.note}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {canEdit && detail.status === 'issued' && (
                  <div className="border-t border-slate-100 pt-4 flex items-center justify-between gap-3">
                    <p className="text-[11px] text-slate-500">Withdrawing ends this offer immediately. The applicant and employer are told the offer was withdrawn — the internal note is never shared with them.</p>
                    <button
                      onClick={() => { setRevokeTarget(detail); setRevokeNote(''); }}
                      className="px-3 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 cursor-pointer transition flex items-center gap-1.5 shrink-0"
                    >
                      <Ban className="h-3.5 w-3.5" /> Withdraw this offer
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Revoke modal */}
      {revokeTarget && (
        <Modal onClose={() => { if (!revokeBusy) setRevokeTarget(null); }} title={`Withdraw offer ${revokeTarget.offerRef || ''}`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900">Withdraw offer {revokeTarget.offerRef || ''}</h3>
              <button aria-label="Close" title="Close" onClick={() => !revokeBusy && setRevokeTarget(null)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                This withdraws the offer to <strong>{revokeTarget.applicantName || 'the applicant'}</strong> for <strong>{revokeTarget.jobTitle || 'the job'}</strong> at <strong>{revokeTarget.employerName || 'the employer'}</strong>.
                The applicant can no longer accept or decline it, and the candidate returns to their previous stage. The offer record and its history are kept.
              </p>
              <div>
                <label htmlFor="revoke-note" className="block text-sm font-bold text-slate-700 mb-1.5">
                  Internal note <span className="text-red-500">(required)</span>
                </label>
                <textarea
                  id="revoke-note"
                  value={revokeNote}
                  onChange={(e) => setRevokeNote(e.target.value)}
                  rows={3}
                  placeholder="Why is this offer being withdrawn? Only ISATS admins can read this."
                  className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-amber-500/30 resize-none"
                />
                <p className="text-xs text-slate-500 mt-1.5">The applicant and employer are told only that the offer was withdrawn — never this note.</p>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => !revokeBusy && setRevokeTarget(null)} disabled={revokeBusy} className="px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer disabled:opacity-50">Cancel</button>
                <button
                  onClick={submitRevoke}
                  disabled={revokeBusy || !revokeNote.trim()}
                  className="px-5 py-2.5 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
                >
                  {revokeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                  Withdraw the offer
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Placement reject modal */}
      {rejectTarget && (
        <Modal onClose={() => { if (queueBusy !== rejectTarget?.id) setRejectTarget(null); }} title="Reject this placement?">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-black text-slate-900">Reject this placement?</h3>
              <button aria-label="Close" title="Close" onClick={() => queueBusy !== rejectTarget.id && setRejectTarget(null)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                The placement of <strong>{rejectTarget.applicantName || 'the applicant'}</strong> for <strong>{rejectTarget.jobTitle || 'the job'}</strong> will be marked Cancelled and kept in the applicant's history.
                The applicant is emailed the outcome, and the employer is notified on their dashboard.
              </p>
              <div>
                <label htmlFor="reject-reason" className="block text-sm font-bold text-slate-700 mb-1.5">Reason <span className="font-semibold text-slate-400">(shared with the applicant)</span></label>
                <textarea
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="Why is this placement not approved?"
                  className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-red-500/30 resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => setRejectTarget(null)} disabled={queueBusy === rejectTarget.id} className="px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer disabled:opacity-50">Cancel</button>
                <button
                  onClick={() => decidePlacement(rejectTarget, 'Cancelled', rejectReason)}
                  disabled={queueBusy === rejectTarget.id}
                  className="px-5 py-2.5 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
                >
                  {queueBusy === rejectTarget.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Reject placement
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showOfferOnBehalf && (
        <AdminOfferOnBehalfModal
          employers={employers}
          onClose={() => setShowOfferOnBehalf(false)}
          onDone={() => { setShowOfferOnBehalf(false); refreshAll(); }}
        />
      )}
      {showInterviewOnBehalf && (
        <AdminInterviewOnBehalfModal
          employers={employers}
          onClose={() => setShowInterviewOnBehalf(false)}
          onDone={() => { setShowInterviewOnBehalf(false); notifyToast('success', 'Interview invitation sent.'); }}
        />
      )}
    </div>
  );
}
